import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import get from "lodash.get";

import TableItem from "../../../ui/TableItem";
import { FACILITY_TYPE } from "../../../constants/intxConstants";
import { DEMAND_PROFILES_PARAM_KEYS } from "../../../constants/storageConstants";
import Percentage from "../../../ui/Percentage";
import TextInput from "../../../ui/TextInput";
import { FACILITY_TYPE_LABELS } from "../../../constants/labelConstants";
import { TIME_PERIOD_TO_LABEL } from "./defaultProfileParamsConstants";
import TotalTableRow from "../../../ui/TotalTableRow";

// these values stored in /public so they can be not minified in the build since PENN DOT wants to be able to edit the values
const { DEMAND_PROFILES } = window.PENN_DOT_CONSTANTS;

/**
 * Renders a table that has one column of default values, one column of inputs,
 * and one column that shows the input value, or the default if nothing was input
 */
export default function DemandProfileForm({
  clearErrors,
  defaultValues,
  errors,
  facilityType,
  formData,
  isTotalValid,
  paramKey,
  profilesType,
  register,
  setError,
  total,
}) {
  // set a root level form error if the total is not valid
  // clear the error when the total becomes valid
  useEffect(() => {
    const errorPath = `root.${paramKey}`;
    const error = get(errors, errorPath);
    if (isTotalValid === true && error) {
      clearErrors(errorPath);
    }
    if (isTotalValid === false && !error) {
      setError(errorPath, {
        message: `${paramKey} profiles must sum to 100%`,
        type: "sumTo100Error",
      });
    }
  }, [clearErrors, errors, isTotalValid, paramKey, setError]);

  return (
    <div>
      <TableContainer
        data-testid={`${paramKey}-profileForm`}
        component={Paper}
        sx={{ maxWidth: "700px" }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="demand volume table">
          <TableHead>
            <TableRow>
              <TableItem header size="small"></TableItem>
              <TableItem header size="small">
                {FACILITY_TYPE_LABELS[facilityType]}
              </TableItem>
              <TableItem header size="small">
                Override Value
              </TableItem>
              <TableItem header size="small">
                Value Applied
              </TableItem>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.values(DEMAND_PROFILES[profilesType]).map(
              ({ timePeriod, ...FACILITY_TYPE_TO_VALUE }) => {
                const hardCodedDefaultValue =
                  FACILITY_TYPE_TO_VALUE[facilityType] * 100;
                const currentOverride = formData?.[timePeriod];
                const name = `${paramKey}.${timePeriod}`;
                return (
                  <TableRow key={timePeriod}>
                    <TableItem size="small" component="th" scope="row">
                      {TIME_PERIOD_TO_LABEL[timePeriod]}
                    </TableItem>
                    <TableItem size="small">
                      <Percentage>{hardCodedDefaultValue}</Percentage>
                    </TableItem>
                    <TableItem size="small">
                      <TextInput
                        defaultValue={defaultValues?.[timePeriod]}
                        endAdornment="%"
                        errors={errors}
                        max={200}
                        min={0}
                        name={name}
                        register={register}
                        size="small"
                        sx={{ minWidth: "100px" }}
                        type="number"
                      />
                    </TableItem>
                    <TableItem size="small">
                      <Percentage>
                        {currentOverride >= 0
                          ? currentOverride
                          : hardCodedDefaultValue}
                      </Percentage>
                    </TableItem>
                  </TableRow>
                );
              }
            )}
            {(!!total || total === 0) && (
              <TotalTableRow
                errorMessage="Must sum to 100%"
                isTotalValid={isTotalValid}
                total={total}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

DemandProfileForm.propTypes = {
  clearErrors: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  errors: PropTypes.object.isRequired,
  facilityType: PropTypes.oneOf(Object.values(FACILITY_TYPE)).isRequired,
  formData: PropTypes.object,
  isTotalValid: PropTypes.bool,
  paramKey: PropTypes.oneOf(Object.values(DEMAND_PROFILES_PARAM_KEYS))
    .isRequired,
  profilesType: PropTypes.oneOf([
    "DAILY_PROFILES",
    "MONTHLY_PROFILES",
    "HOURLY_PROFILES_WEEKDAY",
    "HOURLY_PROFILES_WEEKEND",
  ]).isRequired,
  register: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  total: PropTypes.number,
};
